// src/verticals/useVertical.ts
import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import {
  detectVerticalFromPath,
  LABELS,
  VERTICAL_BY_ID,
  type VerticalId,
} from "./registry";
import {
  getNormalizedHostname,
  isBookingBrandHost,
} from "../util/brandDomains";
import { getRuntimeAppProfile } from "../runtime/appProfile";

const LS_KEY = "lastVertical";

type VerticalDef = (typeof VERTICAL_BY_ID)[VerticalId];

type Ctx = {
  id: VerticalId;
  labels: (typeof LABELS)[VerticalId];
  def: VerticalDef;
};

const VerticalCtx = createContext<Ctx | undefined>(undefined);

function readLastVertical(): VerticalId | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return null;
    let candidate: unknown = raw;
    try {
      candidate = JSON.parse(raw); // legacy JSON.stringify("care")
    } catch {}
    return String(candidate) as VerticalId;
  } catch {
    return null;
  }
}

function writeLastVertical(v: VerticalId) {
  try {
    window.localStorage.setItem(LS_KEY, v);
    window.dispatchEvent(new Event("vertical:changed"));
  } catch {}
}

function detectHostOrAliasVertical(pathname: string): VerticalId | null {
  const normalizedPath = String(pathname || "");
  const host = getNormalizedHostname();

  if (isBookingBrandHost(host)) return "booking";

  if (
    host === "care.plany365.com" ||
    host === "agents.plany365.com" ||
    host === "partners.plany365.com" ||
    host === "carejobs.plany365.com"
  ) {
    return "care";
  }

  if (
    normalizedPath === "/find" ||
    normalizedPath.startsWith("/city/") ||
    normalizedPath.startsWith("/state/")
  ) {
    return "care";
  }

  return null;
}

export function VerticalProvider(props: { children: ReactNode }) {
  const { pathname } = useLocation();

  // Unconditional hooks
  const id = useMemo<VerticalId | null>(() => {
    const fromPath = detectVerticalFromPath(pathname);
    const fromHostOrAlias = detectHostOrAliasVertical(pathname);
    const fromLS = readLastVertical();
    return fromHostOrAlias || fromPath || fromLS || getRuntimeAppProfile().verticalId;
  }, [pathname]);

  useEffect(() => {
    if (id) writeLastVertical(id);
  }, [id]);

  const value = useMemo<Ctx | undefined>(() => {
    if (!id) return undefined;
    return {
      id,
      labels: LABELS[id],
      def: VERTICAL_BY_ID[id],
    };
  }, [id]);

  // Provide context; no JSX
  return React.createElement(
    VerticalCtx.Provider,
    { value: value! },
    props.children
  );
}

export function useVertical(): Ctx {
  const ctx = useContext(VerticalCtx);
  if (!ctx) {
    throw new Error("useVertical must be used within VerticalProvider");
  }
  return ctx;
}

export function withVerticalQuery(path: string, v: VerticalId) {
  const [base, q = ""] = path.split("?");
  const params = new URLSearchParams(q);
  params.set("v", v);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}
